import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Mail, Phone, Lock, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { useToast } from "../contexts/ToastContext";
import api from "../api/axios";

const logo = process.env.PUBLIC_URL + '/logo.png';

function AdminRegisterScreen() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role: "admin"
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Basic client-side validation
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters");
      setLoading(false);
      return;
    }

    try {
      const response = await api.post("/admin/registerAdmin", formData);
      if (response.data.success) {
        addToast("success", "Admin account created successfully!");
        // Redirect to login page
        navigate("/login");
      } else {
        setError(response.data.message || "Registration failed");
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-split-container font-sans">
      {/* Left Panel: Branding & Visuals */}
      <div className="login-left-panel justify-center items-start overflow-hidden relative">
        <div className="absolute inset-0 primary-gradient-bg opacity-90 z-0"></div>
        
        <div className="relative z-10 w-full max-w-lg animate-fadeIn">
          <div className="mb-12">
            <div className="w-24 h-24 bg-white p-1 rounded-full shadow-xl flex items-center justify-center mb-8 transform hover:scale-105 transition-transform duration-500">
               <img src={logo} alt="SmartCampusz Logo" className="w-full h-full object-cover rounded-full" />
            </div>
            <h1 className="text-5xl font-black text-white leading-tight tracking-tight">
              System <br />
              <span className="text-teal-200">Initialization</span>
            </h1>
            <p className="text-blue-50 mt-6 text-lg font-medium max-w-md opacity-90 leading-relaxed">
              Create a new master administrative account for the SmartCampusz. This portal is highly secure.
            </p>
          </div>
          
          <div className="flex gap-4 items-center">
            <div className="h-px w-12 bg-teal-200/50"></div>
            <span className="text-teal-100 text-sm font-bold uppercase tracking-[0.2em]">Secure Setup</span>
          </div>
        </div>

        <svg className="wave-separator" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M0 0 C 40 10, 60 90, 100 100 L 100 0 Z" className="fill-white" />
        </svg>
      </div>

      {/* Right Panel: Form */}
      <div className="login-right-panel animate-fadeIn">
        <div className="w-full max-w-[400px] flex flex-col items-center">
          <div className="w-full text-center lg:text-left mb-8">
            <h2 className="text-3xl font-black text-slate-800 tracking-tight mb-2 flex items-center justify-center lg:justify-start gap-2">
              <ShieldCheck className="text-teal-600 w-8 h-8" /> Secure Setup
            </h2>
            <p className="text-slate-500 font-medium tracking-wide">Register a new Admin Account</p>
          </div>

          <div className="w-full animate-slideInUp">
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm font-medium border border-red-100 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Full Name</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-teal-500 transition-colors">
                    <User size={18} />
                  </div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all font-medium"
                    placeholder="Admin Name"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Email Address</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-teal-500 transition-colors">
                    <Mail size={18} />
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all font-medium"
                    placeholder="admin@smartcampusz.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Phone Number</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-teal-500 transition-colors">
                    <Phone size={18} />
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all font-medium"
                    placeholder="e.g. 9876543210"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Password</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-teal-500 transition-colors">
                    <Lock size={18} />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full pl-10 pr-12 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all font-medium"
                    placeholder="Strong Password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="text-[10px] text-slate-400 mt-1.5 ml-1 font-medium">Must contain uppercase, number, & special char</p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-3 px-4 rounded-xl shadow-lg shadow-teal-600/20 transition-all transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-70 flex justify-center items-center mt-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  "Create Admin Account"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminRegisterScreen;
